// Act I scenario content — verbatim from othellos/CLAUDE_IMPLEMENTATION_BRIEF.md
// and SCENARIO_OUTLINE.md. Story text is data; screens render it. Every
// narration/dialogue string is a `Loc` ({ ko, en }) — speaker/opponent NAMES
// stay Korean (used as stable keys elsewhere: Game.tsx's portrait maps,
// StageCompanion.portraits, ...) and are only translated for display via
// `dName`/`dTitle` in ./loc.ts.

import type { Loc } from "./loc.ts";

export const ACT1_NAME: Loc = { ko: "I. 첫 불빛", en: "I. First Light" };

/** ACT1_PROLOGUE — four opening subtitle lines, shown one at a time. */
export const PROLOGUE_LINES: readonly Loc[] = [
  { ko: "등불 마을을 잃고, 나는 지켜야 할 서약에서 도망쳤다.", en: "I lost my lantern village, and fled the vow I was sworn to keep." },
  { ko: "하지만 스승이 남긴 에메랄드 조각이 다시 붉게 반응했다.", en: "But the emerald shard my teacher left behind flared red again." },
  { ko: "베르다 안개숲의 봉인이 무너지고 있다.", en: "The seal over the Verda Mistwood is breaking." },
  { ko: "이번에는 끝까지 가겠다.", en: "This time, I'm seeing it through." },
];

/** ACT1_TO_ACT2 — Act II opening subtitle over the 협곡 map, after all three
 *  1막 stages are cleared. Same terse first-person narration as the prologue:
 *  companions gathered → what they found → the realisation → where we go next.
 *  Shown once, then the 협곡 pins reveal. */
export const ACT1_TO_ACT2_LINES: readonly Loc[] = [
  { ko: "린과 브람, 세라와 함께 토르를 찾아 길을 떠났다.", en: "With Rin, Bram, and Sera, I set out to find Tor." },
  { ko: "협곡 수로에서 낯선 군대의 흔적을 봤다.", en: "In the gorge's waterways, we found the tracks of a strange army." },
  { ko: "균열은 사고가 아니었다. 침공이다.", en: "The rift was no accident. This is an invasion." },
  { ko: "흔적 끝은 협곡 아래로 향하고 있다.", en: "The trail leads down into the gorge." },
];

export interface PinNode {
  id: number;
  name: string;
  title: string;
  /** Normalised position over the world-map image, 0..1 (x from left, y from top). */
  pos: { x: number; y: number };
  /** Relative size for depth — 브람 (mid) is the 1.0 baseline; 린 is larger
   *  as the nearest pin, 세라 smaller as the furthest up the path. */
  scale: number;
}

export const RIN_ID = 1;
export const BRAM_ID = 2;
export const SERA_ID = 3;

/** Character illustration + scene background paths (public/assets/…). */
export const RIN_PORTRAIT = "/assets/characters/01-rin-moss-gatekeeper-rival-v1.webp";
export const BRAM_PORTRAIT = "/assets/characters/02-bram-lantern-keeper-rival-v1.webp";
export const SERA_PORTRAIT = "/assets/characters/03-sera-ivy-archer-rival-v1.webp";
export const ACT1_BG = "/assets/backgrounds/act-1-moss-gate-v1.webp";
/** World-map background for the 1막 node screen. */
export const ACT1_MAP_BG = "/assets/world-maps/act-1-moss-gate.webp";

/** ACT1_MAP — three pact-stone nodes placed along the forest path, near to
 *  far: 린 on the foreground steps, 브람 at the bend by the green glow just
 *  below the bridge, 세라 on the misty path beyond it. This build wires 린
 *  (stage 1 duel) and 브람 (a story meeting after 린 joins); 세라 stays locked.
 *  `name`/`title` stay Korean — see `dName`/`dTitle` in ./loc.ts. */
export const ACT1_PINS: readonly PinNode[] = [
  { id: 1, name: "린", title: "이끼 문지기", pos: { x: 0.45, y: 0.76 }, scale: 1.25 },
  { id: 2, name: "브람", title: "등불 수집가", pos: { x: 0.5, y: 0.6 }, scale: 1 },
  { id: 3, name: "세라", title: "담쟁이 궁수", pos: { x: 0.35, y: 0.55 }, scale: 0.7 },
];

export type Speaker = "opp" | "player";

export interface DialogueLine {
  speaker: Speaker;
  /** Stable Korean key — used for portrait lookup, translated for display
   *  via `dName`. */
  name: string;
  text: Loc;
}

/** STAGE1_RIN_INTRO — pre-match dialogue, one line at a time. */
export const RIN_INTRO: readonly DialogueLine[] = [
  { speaker: "opp", name: "린", text: { ko: "여기서부터는 이끼 문지기의 길이다. 균열의 앞잡이는 못 지나간다.", en: "From here on, this path belongs to the Moss Gatekeeper. No agent of the rift passes." } },
  { speaker: "player", name: "나", text: { ko: "봉인을 되돌리러 왔다. 스승의 조각이 붉게 탄 뒤로 멈추지 않았어.", en: "I've come to restore the seal. My teacher's shard hasn't stopped burning red since." } },
  { speaker: "opp", name: "린", text: { ko: "네가 균열의 앞잡이가 아니란 건, 말로는 못 믿는다.", en: "Words alone won't prove you aren't the rift's agent." } },
  { speaker: "opp", name: "린", text: { ko: "이 시험을 통과해야 길이 열린다.", en: "Pass this trial, and the path opens." } },
];

/** STAGE1_RIN_OUTRO — after the player wins. 린 accepts the light, joins as
 *  guide, relights the entrance lantern, and points to 브람. (Draft per
 *  SCENARIO_OUTLINE: ally join beat, 길잡이·입구 등불 복구.) */
export const RIN_OUTRO: readonly DialogueLine[] = [
  { speaker: "opp", name: "린", text: { ko: "빛이 흔들리지 않았어. 균열의 것이라면 진작 꺼졌겠지.", en: "Your light didn't waver. If it were the rift's, it would have gone out long ago." } },
  { speaker: "opp", name: "린", text: { ko: "입구의 등불을 다시 밝혀 두지. 어두운 길은 내가 안내한다.", en: "I'll relight the entrance lantern. I'll guide you through the dark stretch." } },
  { speaker: "opp", name: "린", text: { ko: "안개 위쪽에 등불 수집가 브람이 있어. 그를 찾아가.", en: "Up past the mist is Bram, the Lantern Collector. Find him." } },
];

/** ACT1_BRAM_MEET — story meeting on the map after 린 joins; leads into the
 *  브람 duel. 린 travels with the player now, so she speaks here too. 브람
 *  wants to see the dueling light for himself. */
export const BRAM_MEET: readonly DialogueLine[] = [
  { speaker: "opp", name: "브람", text: { ko: "린, 자네가 데려온 건가.", en: "Rin, you brought this one?" } },
  { speaker: "opp", name: "린", text: { ko: "봉인의 빛에 반응한 사람이야. 직접 확인해.", en: "Someone the seal's light answered to. See for yourself." } },
  { speaker: "opp", name: "브람", text: { ko: "나는 흩어진 등불을 모으는 사람이야. 가짜 빛은 금방 티가 나지.", en: "I'm the one who gathers the scattered lanterns. A false light shows itself fast." } },
  { speaker: "player", name: "나", text: { ko: "확인이 필요한가?", en: "You need to check?" } },
  { speaker: "opp", name: "브람", text: { ko: "그래. 등불 아래에서 자네 빛을 직접 시험해 보지.", en: "I do. Let's test your light under the lantern, then." } },
];

/** STAGE2_BRAM_OUTRO — after the player wins the 브람 duel. 브람 joins as the
 *  pathfinder and points ahead to 세라. (Draft per SCENARIO_OUTLINE.) */
export const BRAM_OUTRO: readonly DialogueLine[] = [
  { speaker: "opp", name: "브람", text: { ko: "흔들림 없는 빛이야. 자네가 진짜였군.", en: "Not a flicker in it. You're the real thing." } },
  { speaker: "opp", name: "브람", text: { ko: "안개에 묻힌 길은 내가 안다. 앞은 내가 살피지.", en: "I know the paths the mist buries. I'll scout ahead." } },
  { speaker: "opp", name: "브람", text: { ko: "다음은 세라야. 담쟁이 궁수가 그 너머를 지키고 있어.", en: "Next is Sera. The Ivy Archer guards the way past here." } },
];

/* ── Stage 3 — 세라 + 균열 짐승 (공동 방어) ──────────────────────────────
   Not a 세라 duel: the player fights a 균열 짐승 (the match opponent); 세라 is
   a companion who guards the side path and calls out tactics. 린·브람 travel
   along. Text = CAMPAIGN_DIALOGUE_REWRITE.md §3. */

/** ACT1_SERA_MEET — before the 균열 짐승 match. */
export const SERA_MEET: readonly DialogueLine[] = [
  { speaker: "opp", name: "세라", text: { ko: "균열 짐승이 이 길을 뚫으려 해. 혼자 막기엔 너무 많아.", en: "A rift beast is trying to break through here. There are too many for me alone." } },
  { speaker: "opp", name: "린", text: { ko: "이 사람은 봉인을 되돌리러 왔어. 네 옆을 지킬 수 있을 거야.", en: "This one's come to restore the seal. They can hold the line beside you." } },
  { speaker: "player", name: "나", text: { ko: "좋아. 이 짐승들이 협곡으로 가지 못하게 도와줄게.", en: "Good. I'll help keep these beasts out of the gorge." } },
  { speaker: "opp", name: "세라", text: { ko: "나는 옆길을 지킬게.", en: "I'll hold the side path." } },
];

/** STAGE3_SERA_OUTRO — after the 균열 짐승 is beaten. 세라 joins as scout lead
 *  and points to 토르 / the gorge. */
export const SERA_OUTRO: readonly DialogueLine[] = [
  { speaker: "opp", name: "세라", text: { ko: "짐승이 물러났어. 네 덕분에 길목을 지켰다.", en: "The beast pulled back. We held the pass because of you." } },
  { speaker: "opp", name: "세라", text: { ko: "내가 맡은 다른 무리도 모두 물러났어. 이제 이 길은 안전해.", en: "The other packs I was watching pulled back too. This path's clear now." } },
  { speaker: "opp", name: "브람", text: { ko: "안전하다는 말 들으니 이제야 숨이 쉬어지네. 내 등불도 아까부터 덜덜 떨고 있었어. 아, 내가 떤 건가?", en: "Hearing \"safe\" finally lets me breathe easy. My lantern's been shaking this whole time. Oh — was that me?" } },
  { speaker: "player", name: "나", text: { ko: "협곡으로 들어갈 수 있겠어?", en: "Can we get into the gorge?" } },
  { speaker: "opp", name: "세라", text: { ko: "응. 수로에서 낯선 군대의 흔적을 봤어. 토르가 그 흔적을 쫓고 있다.", en: "Yes. We found tracks of a strange army in the waterways. Tor's been tracking them." } },
  { speaker: "opp", name: "세라", text: { ko: "이제부터 내가 앞을 살필게. 토르를 찾아 함께 협곡으로 가자.", en: "I'll scout ahead from here. Let's find Tor and head into the gorge together." } },
  { speaker: "opp", name: "린", text: { ko: "좋아. 길잡이와 정찰대장이 함께면 안개도 덜 무섭겠어.", en: "Good. With a guide and a scout lead together, even the mist feels less frightening." } },
];

export interface Whisper {
  name: string;
  text: Loc;
}

/** A companion-voiced player-turn line, one 대사 per match state — no event
 *  detection, no auto-dismiss. The match screen picks one each render by
 *  priority: behind > slow > bram > rin > firstTurn(moveNo≤1) > turn. `rin` and
 *  `bram` are one-shot cheers that fire once on the first player turn at/after
 *  their eligible move number (defaults 6 / 14, overridable per stage via
 *  `StageCompanion.rinAtMove` / `bramAtMove`); omit either line to skip that
 *  cheer. `firstTurn` is optional too — without it moveNo≤1 falls back to
 *  `turn`. The opponent turn keeps the stage's `voice.thinking`. */
export interface CompanionTurnLines {
  turn: Whisper;
  slow: Whisper;
  behind: Whisper;
  firstTurn?: Whisper;
  rin?: Whisper;
  bram?: Whisper;
}

export const SERA_TURN_LINES: CompanionTurnLines = {
  firstTurn: { name: "세라", text: { ko: "좋아, 그 자리는 지킬 수 있어. 계속 몰아붙여!", en: "Good, you can hold that spot. Keep pressing!" } },
  turn: { name: "세라", text: { ko: "짐승의 길이 막혔어. 지금 흐름을 잡아!", en: "The beast's path is blocked. Take the momentum now!" } },
  slow: { name: "세라", text: { ko: "조금만 더 버텨. 내가 다른 무리를 막고 있을게!", en: "Just hold a little longer. I've got the other pack!" } },
  behind: { name: "세라", text: { ko: "밀리고 있어. 가장자리부터 되찾아!", en: "We're losing ground. Take the edges back!" } },
  rin: { name: "린", text: { ko: "가운데만 보지 마. 바깥에도 길이 있어.", en: "Don't just watch the center. There's a path on the outside too." } },
  bram: { name: "브람", text: { ko: "한 번에 끝내려 하지 말고, 다음 수를 준비해.", en: "Don't try to end it in one move — set up the next one." } },
};

/** Speaker name → the hero illustration to show while that companion is
 *  talking in the turn bar. Keys match `CompanionTurnLines` entry names. */
export const SERA_COMPANION_PORTRAITS: Record<string, string> = {
  세라: SERA_PORTRAIT,
  린: RIN_PORTRAIT,
  브람: BRAM_PORTRAIT,
};
